import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import EventsUrlPanel from "@/components/EventsUrlPanel";
import EmbedCodeCard from "@/components/EmbedCodeCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MapPin, Check } from "lucide-react";
import { toast } from "sonner";

export default function InstituteSettings() {
  const [institute, setInstitute] = useState(null);
  const [loading, setLoading] = useState(true);
  const [location, setLocation] = useState("");
  const [savingLoc, setSavingLoc] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get("/institutes/me");
        setInstitute(data);
        setLocation(data?.location || "");
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const locationDirty = (location || "") !== (institute?.location || "");

  const saveLocation = async () => {
    setSavingLoc(true);
    try {
      const { data } = await api.put("/institutes/me", { location });
      setInstitute(data);
      setLocation(data?.location || "");
      toast.success("Location saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally {
      setSavingLoc(false);
    }
  };

  return (
    <div className="px-8 lg:px-12 py-12 max-w-4xl" data-testid="institute-settings-page">
      <PageHeader
        overline="Institute admin"
        title="Settings"
        description={institute ? institute.name : "Manage your institute"}
      />

      {loading || !institute ? (
        <p className="text-sm text-[#8A9188] uppercase tracking-[0.2em] font-bold">Loading…</p>
      ) : (
        <div className="space-y-6">
          {/* Location card */}
          <div
            className="bg-white border border-[#E5E2DC] rounded-2xl p-6 sm:p-8"
            data-testid="institute-location-panel"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="h-10 w-10 rounded-xl bg-[#F2F0EA] flex items-center justify-center">
                <MapPin strokeWidth={1.5} className="text-[#B37A5F]" size={18} />
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8A9188]">
                  Discovery
                </p>
                <h3 className="font-heading text-xl text-[#1A1D1A] tracking-tight font-semibold">
                  Location
                </h3>
              </div>
            </div>
            <p className="text-sm text-[#5C635A] mb-5 leading-relaxed max-w-2xl">
              Shown on the public sign-up page so prospects can pick an institute close
              to where they live. City, region, country — whatever helps them choose.
            </p>
            <div className="space-y-2 max-w-2xl mb-4">
              <Label htmlFor="institute-location-input" className="text-xs uppercase tracking-[0.2em] text-[#1A1D1A] font-bold">
                Location
              </Label>
              <Input
                id="institute-location-input"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="e.g. Amsterdam, Netherlands"
                data-testid="institute-location-input"
                className="h-11 rounded-lg border-[#E5E2DC]"
              />
            </div>
            <Button
              onClick={saveLocation}
              disabled={!locationDirty || savingLoc}
              data-testid="institute-location-save-button"
              className="h-11 rounded-lg bg-[#4A5D4E] hover:bg-[#3D4D41] text-white"
            >
              <Check strokeWidth={1.5} size={16} className="mr-2" />
              {savingLoc ? "Saving…" : "Save location"}
            </Button>
          </div>

          <EventsUrlPanel
            institute={institute}
            saveEndpoint="/institutes/me"
            refreshEndpoint="/agenda/events/refresh"
            onSaved={(updated) => setInstitute(updated)}
          />

          {/* Embed code: deep-links prospects to /register step 2 (coach
              picker) with this institute pre-selected. */}
          <EmbedCodeCard
            variant="institute"
            registerUrl={`${typeof window !== "undefined" ? window.location.origin : ""}/register?institute=${encodeURIComponent(institute.id)}`}
          />
        </div>
      )}
    </div>
  );
}
